import { useEffect, useState } from "react";
import VoiceTextarea from "../VoiceTextarea";

/**
 * <ServiceRatingBlock /> — extracted from JobCardDetail.jsx (Wave 25.16
 * Phase 2 refactor). Three-perspective service rating block — populates
 * fields:
 *   rating_customer / rating_technician / rating_manager + matching notes.
 * Customer rating is captured at sign-off (read-only here; flows in via
 * the customer signature pad). Technician self-rates after the visit.
 * Manager scores during debrief.
 */
export default function ServiceRatingBlock({ job, canEditAll, canEditTech, onChange }) {
  return (
    <div className="hv-card p-5" data-testid="job-service-rating">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-display text-xl font-bold">Service rating</h3>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
          internal metric · 1 = poor · 5 = exceptional
        </span>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <RatingRow
          title="Customer"
          subtitle="Score the visit (the customer can also tap a star)"
          value={job.rating_customer}
          notes={job.rating_customer_notes}
          canEdit={canEditAll}
          onChange={(v, n) => onChange({ rating_customer: v, rating_customer_notes: n })}
          testid="rating-customer"
          accent="#34D399"
        />
        <RatingRow
          title="Technician"
          subtitle="How did the visit go from your side?"
          value={job.rating_technician}
          notes={job.rating_technician_notes}
          canEdit={canEditTech}
          onChange={(v, n) => onChange({ rating_technician: v, rating_technician_notes: n })}
          testid="rating-technician"
          accent="#60A5FA"
        />
        <RatingRow
          title="Manager debrief"
          subtitle="Quality / pace / outcome from review"
          value={job.rating_manager}
          notes={job.rating_manager_notes}
          canEdit={canEditAll}
          onChange={(v, n) => onChange({ rating_manager: v, rating_manager_notes: n })}
          testid="rating-manager"
          accent="#A78BFA"
        />
      </div>
    </div>
  );
}

function RatingRow({ title, subtitle, value, notes, canEdit, onChange, testid, accent }) {
  const [draftNotes, setDraftNotes] = useState(notes || "");
  useEffect(() => {
    setDraftNotes(notes || "");
  }, [notes]);
  return (
    <div className="border border-white/5 rounded-md p-3 bg-white/[0.02]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: accent }}>
        {title}
      </div>
      <div className="text-[10px] text-[#647087] mb-2">{subtitle}</div>
      <div className="flex items-center gap-1 mb-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            disabled={!canEdit}
            onClick={() => onChange(n === value ? null : n, notes)}
            className={`w-7 h-7 rounded-md text-sm transition ${
              value && n <= value
                ? "border-2 text-white"
                : "border border-white/10 text-[#647087] hover:text-white"
            } ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}
            style={
              value && n <= value
                ? { background: `${accent}30`, borderColor: accent }
                : {}
            }
            data-testid={`${testid}-star-${n}`}
            aria-label={`Rate ${n}`}
          >
            ★
          </button>
        ))}
      </div>
      {canEdit ? (
        <VoiceTextarea
          value={draftNotes}
          onChange={setDraftNotes}
          onBlur={() => {
            if (draftNotes !== (notes || "")) onChange(value, draftNotes);
          }}
          onCommit={({ refined }) => {
            // Voice path: dictation commits straight through (no manual
            // blur step). Persist immediately so the rep doesn't lose it.
            setDraftNotes(refined);
            if (refined !== (notes || "")) onChange(value, refined);
          }}
          placeholder="Optional debrief notes…"
          className="text-xs min-h-[42px]"
          context="service_rating_notes"
          testIdPrefix={`${testid}-notes`}
          data-testid={`${testid}-notes`}
        />
      ) : notes ? (
        <div className="text-[11px] text-[#dde3ec] whitespace-pre-wrap opacity-90">{notes}</div>
      ) : null}
    </div>
  );
}
